/**
 * Toolbar + dialogs for column pickers and per-column text filters, wired by {@link useConfiguredTable}.
 * Uses styled divs (not MUI Box) for layout only; interactive controls go through shared form/button components.
 */
import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import AppButton from "../Button/Button";
import AppDialog from "../Dailog/Dailog";
import AppCheckbox from "../FormInputs/AppCheckbox";
import FormInput from "../FormInputs/MuiTextField";
import { LabelText } from "../Typography/AppTypography";

const ControlsToolbar = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "flex-end",
  marginBottom: theme.spacing(1),
}));

const DialogBody = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
}));

const ColumnsActionRow = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
  flexWrap: "wrap",
}));

const ColumnsGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
  gridTemplateColumns: "1fr",
  [theme.breakpoints.up("sm")]: {
    gridTemplateColumns: "1fr 1fr",
  },
  [theme.breakpoints.up("md")]: {
    gridTemplateColumns: "1fr 1fr 1fr",
  },
}));

// Theme-aware tile styling driven by data attributes so we avoid invalid DOM props (e.g. passing `checked` to a div).
const ColumnOptionTile = styled("div")(({ theme }) => ({
  border: "1px solid",
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5, 1),
  borderColor: theme.palette.divider,
  backgroundColor: "transparent",
  opacity: 1,
  '&[data-checked="true"]': {
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(255,152,0,0.06)",
  },
  '&[data-locked="true"]': {
    opacity: 0.8,
  },
}));

const TableControls = ({
  allColumns = [],
  visibleColumnKeys = [],
  defaultVisibleColumns = [],
  onApplyVisibleColumns,
  filterableColumns = [],
  filters = {},
  onApplyFilters,
  onClearFilters,
  showFiltersButton = true,
  showColumnsButton = true,
}) => {
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [pendingVisibleKeys, setPendingVisibleKeys] = useState(visibleColumnKeys);
  const [pendingFilters, setPendingFilters] = useState(filters || {});
  const [filterError, setFilterError] = useState("");

  const openColumnDialog = () => {
    setPendingVisibleKeys(visibleColumnKeys);
    setColumnDialogOpen(true);
  };

  const closeColumnDialog = () => {
    setColumnDialogOpen(false);
    setPendingVisibleKeys(visibleColumnKeys);
  };

  const togglePendingColumn = (key) => {
    setPendingVisibleKeys((prev) => {
      const exists = prev.includes(key);
      return exists ? prev.filter((k) => k !== key) : [...prev, key];
    });
  };

  const applyPendingColumns = () => {
    onApplyVisibleColumns?.(pendingVisibleKeys);
    setColumnDialogOpen(false);
  };

  const handleSelectAll = () => {
    setPendingVisibleKeys(allColumns.map((c) => c.key));
  };

  const handleDeselectAll = () => {
    // Keep locked columns visually selected; they are always visible.
    const lockedKeys = allColumns.filter((c) => c.locked).map((c) => c.key);
    setPendingVisibleKeys(lockedKeys);
  };

  const handleResetDefaults = () => {
    setPendingVisibleKeys(defaultVisibleColumns);
  };

  const handleApplyFilters = () => {
    const sanitizedFilters = Object.entries(pendingFilters || {}).reduce(
      (acc, [key, val]) => {
        const trimmed = String(val ?? "").trim();
        if (trimmed !== "") acc[key] = trimmed;
        return acc;
      },
      {}
    );
    const hasNonEmpty = Object.keys(sanitizedFilters).length > 0;
    if (!hasNonEmpty) {
      setFilterError("Add at least one filter before applying.");
      return;
    }
    setFilterError("");
    onApplyFilters?.(sanitizedFilters);
    setPendingFilters(sanitizedFilters);
    setFilterDialogOpen(false);
  };

  const openFilterDialog = () => {
    setPendingFilters(filters || {});
    setFilterError("");
    setFilterDialogOpen(true);
  };

  const closeFilterDialog = () => {
    setFilterDialogOpen(false);
    setFilterError("");
    setPendingFilters(filters || {});
  };

  return (
    <React.Fragment>
      <ControlsToolbar>
        {showFiltersButton && (
          <AppButton variant="primary" size="small" onClick={openFilterDialog}>
            Filters
          </AppButton>
        )}
        {showColumnsButton && (
          <AppButton variant="primary" size="small" onClick={openColumnDialog}>
            Columns
          </AppButton>
        )}
      </ControlsToolbar>

      <AppDialog
        open={columnDialogOpen}
        onClose={closeColumnDialog}
        title="Select Columns"
        showCloseButton
        maxWidth="sm"
        sx={{
          "& .MuiDialog-paper": {
            maxHeight: "90vh",
          },
        }}
        contentSx={{
          maxHeight: "62vh",
          overflowY: "auto",
        }}
        actions={[
          { label: "Reset Default", variant: "ghost", onClick: handleResetDefaults },
          { label: "Done", variant: "primary", onClick: applyPendingColumns },
        ]}
      >
        <DialogBody>
          <ColumnsActionRow>
            <AppButton variant="ghost" size="small" onClick={handleSelectAll}>
              Select All
            </AppButton>
            <AppButton variant="ghost" size="small" onClick={handleDeselectAll}>
              Deselect All
            </AppButton>
          </ColumnsActionRow>
          <ColumnsGrid>
            {allColumns.map((column) => {
              const checked = pendingVisibleKeys.includes(column.key);
              return (
                <ColumnOptionTile
                  key={column.key}
                  data-checked={checked ? "true" : "false"}
                  data-locked={column.locked ? "true" : "false"}
                >
                  <AppCheckbox
                    checked={checked}
                    disabled={column.locked}
                    onChange={() => togglePendingColumn(column.key)}
                    size="small"
                    label={`${column.label}${column.locked ? " (Always Visible)" : ""}`}
                    sx={{ m: 0, width: "100%" }}
                  />
                </ColumnOptionTile>
              );
            })}
          </ColumnsGrid>
        </DialogBody>
      </AppDialog>

      <AppDialog
        open={filterDialogOpen}
        onClose={closeFilterDialog}
        title="Column Filters"
        showCloseButton
        maxWidth="sm"
        sx={{
          "& .MuiDialog-paper": {
            maxHeight: "90vh",
          },
        }}
        contentSx={{
          maxHeight: "62vh",
          overflowY: "auto",
        }}
        actions={[
          { label: "Clear", variant: "ghost", onClick: () => { setPendingFilters({}); onClearFilters?.(); setFilterError(""); } },
          { label: "Apply", variant: "primary", onClick: handleApplyFilters },
        ]}
      >
        <DialogBody>
          {filterError ? (
            <LabelText sx={{ color: "error.main", mb: 1, display: "block" }}>
              {filterError}
            </LabelText>
          ) : null}
          {filterableColumns.length === 0 && (
            <LabelText>No configured filters available.</LabelText>
          )}
          {filterableColumns.map((column) => (
            <FormInput
              key={column.key}
              type="text"
              name={column.key}
              label={column.label}
              value={pendingFilters[column.key] || ""}
              onChange={(event) => {
                const raw = event.target.value ?? "";
                setPendingFilters((prev) => ({ ...prev, [column.key]: raw }));
                if (filterError) setFilterError("");
              }}
              fullWidth
              size="small"
              margin="dense"
            />
          ))}
        </DialogBody>
      </AppDialog>
    </React.Fragment>
  );
};

export default TableControls;
